import { useNavigate } from 'react-router-dom'

export default function PrivacyPolicy() {
  const navigate = useNavigate()

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B1E3D',
      color: '#F5EDD8',
      fontFamily: '"DM Sans", sans-serif',
      padding: '80px 24px 60px',
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>

        {/* Back */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none', border: 'none', color: 'rgba(245,237,216,0.45)',
            cursor: 'pointer', fontSize: 14, fontFamily: '"DM Sans", sans-serif',
            marginBottom: 40, padding: 0, display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          ← Back to WaveSync
        </button>

        {/* Header */}
        <div style={{
          fontFamily: '"Playfair Display", serif',
          fontSize: 18, color: '#E8614A', fontWeight: 700, marginBottom: 16,
        }}>
          🌊 WaveSync
        </div>
        <h1 style={{
          fontFamily: '"Playfair Display", serif',
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 900, color: '#fff', margin: '0 0 12px',
        }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(245,237,216,0.35)', marginBottom: 56, borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: 32 }}>
          Last Updated: May 13, 2026 · WaveSync LLC
        </p>

        <p style={p}>
          WaveSync LLC ("WaveSync," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, store, and share information when you use the WaveSync platform — a cloud-based
          management and scheduling tool for surf schools and water sports operators.
        </p>
        <p style={p}>By using the Platform, you agree to the practices described in this Privacy Policy.</p>

        <Section>1. Information We Collect</Section>
        <Sub>1.1 Information You Provide</Sub>
        <p style={p}>When you register and use WaveSync, we collect:</p>
        <ul style={ul}>
          <li><strong>Account information:</strong> Name, email address, business name, and password</li>
          <li><strong>Billing information:</strong> Payment details processed securely through Stripe (WaveSync does not store full card numbers)</li>
          <li><strong>Business data:</strong> Scheduling information, staff details, class/session records, and other data you input</li>
          <li><strong>Customer data:</strong> Information about your students and clients, including names, contact information, and booking history</li>
        </ul>

        <Sub>1.2 Information Collected Automatically</Sub>
        <ul style={ul}>
          <li>Log data (IP address, browser type, pages visited, timestamps)</li>
          <li>Device information (device type, operating system)</li>
          <li>Usage data (features used, actions taken within the Platform)</li>
          <li>Cookies and similar tracking technologies</li>
        </ul>

        <Sub>1.3 Information from Third Parties</Sub>
        <p style={p}>
          We may receive information from third-party services integrated with the Platform, such as Stripe for
          payment processing purposes.
        </p>

        <Section>2. How We Use Your Information</Section>
        <ul style={ul}>
          <li>Provide, operate, and maintain the Platform</li>
          <li>Process payments and manage your subscription</li>
          <li>Send transactional emails (receipts, booking confirmations, account notices)</li>
          <li>Respond to your support requests and inquiries</li>
          <li>Improve and develop new Platform features</li>
          <li>Monitor usage patterns and diagnose technical issues</li>
          <li>Comply with legal obligations</li>
          <li>Send product updates and marketing communications (you may opt out at any time)</li>
        </ul>

        <Section>3. How We Store Your Information</Section>
        <p style={p}>
          Your data is stored using <strong style={{ color: '#F5EDD8' }}>Supabase</strong>, a secure cloud database
          platform. We implement industry-standard security measures including encryption in transit (TLS/SSL) and at
          rest. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
        </p>

        <Section>4. How We Share Your Information</Section>
        <p style={p}>We do not sell your personal information. We may share your information in these limited circumstances:</p>

        <Sub>4.1 Service Providers</Sub>
        <ul style={ul}>
          <li><strong>Stripe</strong> — payment processing</li>
          <li><strong>Supabase</strong> — data storage and database services</li>
        </ul>
        <p style={p}>
          These providers are contractually obligated to protect your data and may only use it to provide services
          to WaveSync.
        </p>

        <Sub>4.2 Legal Requirements</Sub>
        <p style={p}>
          We may disclose your information if required by law, court order, or governmental authority, or to protect
          the rights, property, or safety of WaveSync, our users, or the public.
        </p>

        <Sub>4.3 Business Transfers</Sub>
        <p style={p}>
          In the event of a merger, acquisition, or sale of WaveSync's assets, your information may be transferred.
          We will notify you of any such change.
        </p>

        <Section>5. Your Customers' Data</Section>
        <p style={p}>
          As a surf school operator using WaveSync, you are responsible for the personal data you collect from your
          students and clients. You are the data controller for that data, and WaveSync acts as a data processor on
          your behalf. You are responsible for ensuring compliance with all applicable privacy laws.
        </p>

        <Section>6. Cookies</Section>
        <p style={p}>We use cookies and similar technologies to:</p>
        <ul style={ul}>
          <li>Keep you logged in to your account</li>
          <li>Remember your preferences</li>
          <li>Analyze Platform usage</li>
        </ul>
        <p style={p}>
          You can control cookies through your browser settings, though disabling them may affect Platform functionality.
        </p>

        <Section>7. Data Retention</Section>
        <p style={p}>
          We retain your data for as long as your account is active or as needed to provide services. If you cancel,
          you may request a data export within 30 days. After 30 days following account termination, we may delete
          your data, except where required for legal or compliance purposes.
        </p>

        <Section>8. Your Rights and Choices</Section>
        <ul style={ul}>
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate data</li>
          <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal obligations</li>
          <li><strong>Opt-out:</strong> Unsubscribe from marketing emails at any time</li>
          <li><strong>Data portability:</strong> Request an export of your data in a portable format</li>
        </ul>
        <p style={p}>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:drew@wavesync.cloud" style={link}>drew@wavesync.cloud</a>.
        </p>

        <Section>9. Children's Privacy</Section>
        <p style={p}>
          The Platform is intended for use by businesses and is not directed at children under the age of 13. We do
          not knowingly collect personal information from children under 13. Contact us immediately if you believe
          we have inadvertently collected such information.
        </p>

        <Section>10. Third-Party Links</Section>
        <p style={p}>
          The Platform may contain links to third-party websites or services. We are not responsible for their
          privacy practices and encourage you to review their privacy policies.
        </p>

        <Section>11. Changes to This Policy</Section>
        <p style={p}>
          We may update this Privacy Policy from time to time. We will notify you of material changes by posting
          the updated policy on the Platform and/or sending an email notification.
        </p>

        <Section>12. Contact Us</Section>
        <p style={p}>
          For questions or requests regarding this Privacy Policy, contact us at:{' '}
          <a href="mailto:drew@wavesync.cloud" style={link}>drew@wavesync.cloud</a>
          <br />
          WaveSync LLC · 1690 Newtown Langhorne Rd, ATTN 4565, Newtown, PA 18940
        </p>

        {/* Footer */}
        <div style={{
          marginTop: 64, paddingTop: 28,
          borderTop: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <button onClick={() => navigate('/terms')} style={footerBtn}>Terms of Service</button>
          <button onClick={() => navigate('/')} style={footerBtn}>Back to Home</button>
        </div>
      </div>
    </div>
  )
}

const p = {
  fontSize: 15, lineHeight: 1.75,
  color: 'rgba(245,237,216,0.65)',
  marginBottom: 16,
}
const ul = {
  fontSize: 15, lineHeight: 1.75,
  color: 'rgba(245,237,216,0.65)',
  paddingLeft: 24, marginBottom: 16,
}
const link = { color: '#0EAAA4', textDecoration: 'none' }
const footerBtn = {
  background: 'none', border: 'none',
  color: 'rgba(245,237,216,0.4)', cursor: 'pointer',
  fontSize: 13, fontFamily: '"DM Sans", sans-serif', padding: 0,
}

function Section({ children }) {
  return (
    <h2 style={{
      fontFamily: '"Playfair Display", serif',
      fontSize: 20, fontWeight: 700,
      color: '#fff', marginTop: 44, marginBottom: 12,
    }}>{children}</h2>
  )
}

function Sub({ children }) {
  return (
    <h3 style={{
      fontSize: 15, fontWeight: 700,
      color: '#E8614A', marginTop: 20, marginBottom: 8,
    }}>{children}</h3>
  )
}
